import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { requestPasswordReset } from '../lib/api';
import AuthLayout from './AuthLayout';

type Status = 'idle' | 'submitting' | 'sent' | 'error';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setStatus('submitting');
    setError(null);

    const result = await requestPasswordReset(email.trim());
    if (result.success) {
      setStatus('sent');
    } else {
      setStatus('error');
      setError(result.error ?? 'Something went wrong. Please try again.');
    }
  }

  if (status === 'sent') {
    return (
      <AuthLayout
        title="Check your email"
        subtitle="If an account exists for this email address, we've sent password reset instructions."
      >
        <p className="auth-help">
          Didn't receive an email? Check your spam folder, or{' '}
          <button type="button" className="auth-link-button" onClick={() => setStatus('idle')}>
            try again
          </button>
          .
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Forgot your password?"
      subtitle="Enter the email for your account and we'll send you a link to reset it."
    >
      <form onSubmit={handleSubmit} className="auth-form" noValidate>
        <label className="auth-field">
          <span>Email</span>
          <input
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={event => setEmail(event.target.value)}
            disabled={status === 'submitting'}
          />
        </label>

        {error ? (
          <p className="auth-error" role="alert">
            {error}
          </p>
        ) : null}

        <button
          type="submit"
          className="auth-submit"
          disabled={status === 'submitting' || email.trim().length === 0}
        >
          {status === 'submitting' ? 'Sending…' : 'Send reset link'}
        </button>
      </form>

      <p className="auth-help">
        Remembered your password? <Link to="/login">Back to sign in</Link>
      </p>
    </AuthLayout>
  );
}
